import React, { useEffect, useState } from "react";
import Box from "@amzn/meridian/esm/components/box";
import Column from "@amzn/meridian/esm/components/column";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme/theme";
import summaryStopped from "../../images/summary_stop.svg";
import noSummary from "../../images/red_no_summary.svg";
import { AIMS_STATUS } from "../../constants";

/**
 * Renders a generic summary-stopped screen based on AiMS status.
 * Displays different messages and visuals for NOT_AVAILABLE, SUMMARY_TO_ORGANIZER, and AVAILABLE.
 *
 * @param {Object} props
 * @param {string} props.aimsStatus - One of AIMS_STATUS.NOT_AVAILABLE | SUMMARY_TO_ORGANIZER | AVAILABLE
 */
const SummarizeStopped = ({ aimsStatus: initAimsStatus }) => {
  const [aimsStatus, setAimsStatus] = useState(initAimsStatus);

  useEffect(() => {
    if (window.location.href === "http://localhost:3000/") {
      console.log("[Localhost] setSummaryMessage as NORMAL");
      setAimsStatus(AIMS_STATUS.EXTERNAL_ATTENDEE);
    }
  }, []);

  if (aimsStatus === AIMS_STATUS.AVAILABLE) return <SummaryAvailable />;
  if (aimsStatus === AIMS_STATUS.NOT_AVAILABLE) return <NoSummary />;
  if (aimsStatus === AIMS_STATUS.SUMMARY_TO_ORGANIZER) return <SummaryToOrganizerOnly />;
  if (aimsStatus === AIMS_STATUS.EXTERNAL_ATTENDEE) return <MeetingHasExternalAttendee />;

  return null;
};

const SummaryAvailable = () => (
  <StoppedTemplate
    image={summaryStopped}
    alt="summaryStopped"
    header="Summarizing meeting stopped"
    message="You’ll receive a meeting summary when it’s ready."
  />
);

//TODO: integrate with backend
const NoSummary = () => (
  <StoppedTemplate
    image={noSummary}
    alt="organizerNoSummary"
    header="No meeting summary"
    message={
      <>
        There is no meeting summary because the meeting topics are not approved for Amazon Meetings Summary.
        <br />
        <br />
        Review{" "}
        <a
          href="https://w.amazon.com/bin/view/UnifiedCommunications/MeetingSummaries/AiMS"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#008060", textDecoration: "underline" }}
        >
          Amazon policy
        </a>{" "}
        for meeting recordings, transcriptions, and AI summarization.
      </>
    }
  />
);

const SummaryToOrganizerOnly = () => (
  <StoppedTemplate
    image={summaryStopped}
    alt="summaryStopped"
    header="Summarizing meeting stopped"
    message="Only you will receive the meeting summary when it’s ready."
  />
);

//TODO: integration with backend
const MeetingHasExternalAttendee = () => (
  <StoppedTemplate
    image={summaryStopped}
    alt="summaryStopped"
    header="Summarizing meeting stopped"
    message={
      <>
        You’ll receive a meeting summary when it’s ready.
        <br />
        <br />
        External participants won’t receive a meeting summary. Share the meeting summary with them when appropriate.
      </>
    }
  />
);

const StoppedTemplate = ({ image, alt, header, message }) => (
  <Column alignmentHorizontal="start" width="100%" position="relative" spacing="none" spacingInset="none">
    <Row spacing="none" spacingInset="none">
      <Theme tokens={{ boxBorderColorOutline: "#FFFFFF" }}>
        <Box type="outline" spacingInset="none" minWidth="15px" />
      </Theme>
      <Box width="340px" position="relative">
        <img src={image} alt={alt} style={{ display: "block", margin: "auto", maxWidth: "100%", height: "auto" }} />
      </Box>
    </Row>
    <Column height="16px" spacing="none" />
    <Column spacing="none">
      <Row spacing="none" spacingInset="none">
        <Theme tokens={{ boxBorderColorOutline: "#FFFFFF" }}>
          <Box type="outline" spacingInset="none" minWidth="68px" />
        </Theme>
        <Text fontFamily="amazonEmber" type="h50" alignment="start">
          {header}
        </Text>
      </Row>
    </Column>
    <Column height="16px" spacing="none" />
    <Column spacing="none" spacingInset="none">
      <Row spacing="none" spacingInset="none">
        <Theme tokens={{ boxBorderColorOutline: "#FFFFFF" }}>
          <Box type="outline" spacingInset="none" minWidth="68px" />
        </Theme>
        <Theme tokens={{ boxBorderColorOutline: "#FFFFFF" }}>
          <Box type="outline" spacingInset="none" maxWidth="239px">
            <Theme tokens={{ textListSpacingVertical: "160px" }}>
              <Text fontFamily="amazonEmber" type="b200" alignment="start" color="secondary">
                {message}
              </Text>
            </Theme>
          </Box>
        </Theme>
      </Row>
    </Column>
  </Column>
);

export default SummarizeStopped;
